import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../../common/CCUtils"
import { log } from "../../../../common/Logging"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { BackgroundSprite } from "../../../generic-sprites/BackgroundSprite"
import { Buttons } from "../../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { ThrobberSprite } from "../../../generic-sprites/ThrobberSprite"
import { AudioSync } from "../../../utils/AudioSync"
import { CameraZoom, ZoomInfo } from "../../../utils/CameraZoom"
import { colorNum, ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { SecondHandRepo } from "../SecondHandRepo"
import { loadSharedAssets, SharedSecondHandAssets } from "../SharedSecondHandAssets"
import { loadAssets, SecondVsDirectAssets } from "./SecondVsDirectAssets"
import { SvdPos } from "./SvdPos"

export class SecondVsDirectState extends Phaser.State {

    private smokeBoxFront: Phaser.Button
    private smokeBoxSmoke: Phaser.Image
    private vaporBoxFront: Phaser.Button
    private introAudio: Phaser.Sound
    private cig: Phaser.Sprite
    private cigAnim: Phaser.Animation
    private smokeSmokeLeft: Phaser.Sprite
    private smokeSmokeRight: Phaser.Sprite
    private vaporSmokeLeft: Phaser.Sprite
    private vaporSmokeRight: Phaser.Sprite
    private smokeLeftLungs: ThrobberSprite
    private smokeRightLungs: ThrobberSprite
    private vaporLeftLungs: ThrobberSprite
    private vaporRightLungs: ThrobberSprite
    private oneDone = false

    preload() {
        loadAssets(this.game)
        loadSharedAssets(this.game)
        loadGenericAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)

        const boxScale = 0.4
        const bodyScale = 0.59
        const lungScale = 0.35

        this.game.add.existing(new BackgroundSprite(this.game, SecondVsDirectAssets.BACKGROUND))

        const smokeBoxBack = this.game.add.image(SvdPos.smokeBoxX(), SvdPos.boxY(), SharedSecondHandAssets.BOX_BACK)
        Scale.scaleSprite(smokeBoxBack, boxScale)
        const vaporBoxBack = this.game.add.image(SvdPos.vaporBoxX(), SvdPos.boxY(), SharedSecondHandAssets.BOX_BACK)
        Scale.scaleSprite(vaporBoxBack, boxScale)

        const smokeLeftBody = this.game.add.image(SvdPos.smokeLeftBodyX(), SvdPos.bodyY(), SecondVsDirectAssets.BODY)
        Scale.scaleSprite(smokeLeftBody, bodyScale)
        const smokeRightBody = this.game.add.image(SvdPos.smokeRightBodyX(), SvdPos.bodyY(), SecondVsDirectAssets.BODY)
        Scale.scaleSprite(smokeRightBody, bodyScale)

        const vaporLeftBody = this.game.add.image(SvdPos.vaporLeftBodyX(), SvdPos.bodyY(), SecondVsDirectAssets.BODY)
        Scale.scaleSprite(vaporLeftBody, bodyScale)
        const vaporRightBody = this.game.add.image(SvdPos.vaporRightBodyX(), SvdPos.bodyY(), SecondVsDirectAssets.BODY)
        Scale.scaleSprite(vaporRightBody, bodyScale)

        this.smokeLeftLungs = new ThrobberSprite(this.game, SvdPos.smokeLeftLungsX(), SvdPos.lungsY(),
            SecondVsDirectAssets.SMOKER_LUNGS_FLIPPED, lungScale * 2)
        this.smokeLeftLungs.frame = 7
        this.game.add.existing(this.smokeLeftLungs)
        this.smokeLeftLungs.throb()
        this.smokeRightLungs =
            new ThrobberSprite(this.game, SvdPos.smokeRightLungsX(), SvdPos.lungsY(), SecondVsDirectAssets.SMOKER_LUNGS,
                lungScale * 2)
        this.game.add.existing(this.smokeRightLungs)
        this.smokeRightLungs.throb()

        this.vaporLeftLungs = new ThrobberSprite(this.game, SvdPos.vaporLeftLungsX(), SvdPos.lungsY(),
            SecondVsDirectAssets.SMOKER_LUNGS_FLIPPED, lungScale * 2)
        this.vaporLeftLungs.frame = 7
        this.game.add.existing(this.vaporLeftLungs)
        this.vaporLeftLungs.throb()
        this.vaporRightLungs =
            new ThrobberSprite(this.game, SvdPos.vaporRightLungsX(), SvdPos.lungsY(), SecondVsDirectAssets.SMOKER_LUNGS,
                lungScale * 2)
        this.game.add.existing(this.vaporRightLungs)
        this.vaporRightLungs.throb()

        this.cig = this.game.add.sprite(SvdPos.cigX(), SvdPos.cigY(), SecondVsDirectAssets.CIG_FLIPPED)
        this.cig.frame = 5
        Scale.scaleSprite(this.cig, lungScale)
        this.cigAnim = this.cig.animations.add("cigStart", Sprites.reversedSpriteFrames(6, 3))
        const cigLoopAnim = this.cig.animations.add("cigLoop", [12, 13, 14, 15, 16, 17, 16, 15, 14, 13])
        this.cigAnim.onComplete.add(() => {
            cigLoopAnim.play(8, true)
        })

        const vape = this.game.add.sprite(SvdPos.vapeX(), SvdPos.vapeY(), SecondVsDirectAssets.VAPE_PEN)
        Scale.scaleSprite(vape, 0.1)

        this.smokeSmokeLeft =
            this.game.add.sprite(SvdPos.smokeSmokeLeft(), SvdPos.smokeY(), SecondVsDirectAssets.SMOKE_ANIM_FLIPPED)
        Scale.scaleSprite(this.smokeSmokeLeft, lungScale * 2)
        this.smokeSmokeLeft.visible = false

        this.smokeSmokeRight =
            this.game.add.sprite(SvdPos.smokeSmokeRight(), SvdPos.smokeY(), SecondVsDirectAssets.SMOKE_ANIM)
        Scale.scaleSprite(this.smokeSmokeRight, lungScale * 2)
        this.smokeSmokeRight.visible = false

        this.vaporSmokeLeft =
            this.game.add.sprite(SvdPos.vaporSmokeLeft(), SvdPos.smokeY(), SecondVsDirectAssets.VAPOR_ANIM_FLIPPED)
        Scale.scaleSprite(this.vaporSmokeLeft, lungScale * 2)
        this.vaporSmokeLeft.visible = false

        this.vaporSmokeRight =
            this.game.add.sprite(SvdPos.vaporSmokeRight(), SvdPos.smokeY(), SecondVsDirectAssets.VAPOR_ANIM)
        Scale.scaleSprite(this.vaporSmokeRight, lungScale * 2)
        this.vaporSmokeRight.visible = false

        this.smokeBoxSmoke = this.game.add.image(SvdPos.smokeBoxX(), SvdPos.boxY(), SharedSecondHandAssets.BOX_SMOKE)
        Scale.scaleSprite(this.smokeBoxSmoke, boxScale)
        this.smokeBoxSmoke.alpha = 0

        this.smokeBoxFront = this.game.add.button(SvdPos.smokeBoxX(), SvdPos.boxY(), SharedSecondHandAssets.BOX_FRONT)
        Scale.scaleSprite(this.smokeBoxFront, boxScale)
        this.vaporBoxFront = this.game.add.button(SvdPos.vaporBoxX(), SvdPos.boxY(), SharedSecondHandAssets.BOX_FRONT)
        Scale.scaleSprite(this.vaporBoxFront, boxScale)

        const smokeTitle = this.game.add.image(SvdPos.smokeTitleX(), SvdPos.titleY(), SecondVsDirectAssets.SMOKE_TITLE)
        Scale.scaleSprite(smokeTitle, 0.65)
        const vaporTitle = this.game.add.image(SvdPos.vaporTitleX(), SvdPos.titleY(), SecondVsDirectAssets.VAPOR_TITLE)
        Scale.scaleSprite(vaporTitle, 0.65)

        if (!CCUtils.debug) {
            this.smokeBoxFront.inputEnabled = false
            this.vaporBoxFront.inputEnabled = false
        }

        this.introAudio = this.game.add.audio(SecondVsDirectAssets.INTRO)
        this.introAudio.onStop.addOnce(() => {
            Buttons.makeSpriteButton(this.smokeBoxFront)
            Buttons.makeSpriteButton(this.vaporBoxFront)
            this.smokeBoxFront.events.onInputDown.addOnce(this.smoke)
            this.vaporBoxFront.events.onInputDown.addOnce(this.vapor)
        })
        this.introAudio.play()
    }

    playAudioAndZoom = (audio: Phaser.Sound, oneOffs, loopsTimer) => {
        loopsTimer.start()
        const waitForZoom = this.game.time.create()
        waitForZoom.add(1000, () => {
            oneOffs.start()
            this.introAudio.stop()
            this.cigAnim.play(8)

        })
        waitForZoom.start()
    }

    smoke = () => {
        const undoZoom = CameraZoom.zoom(this.game, this.smokeBoxFront)
        const smokeAudio = this.game.add.audio(SecondVsDirectAssets.SMOKING_AUDIO)
        const oneOffs = new AudioSync(this.game, smokeAudio)
        const loopsTimer = this.game.time.create(false)
        this.playAudioAndZoom(smokeAudio, oneOffs, loopsTimer)

        const turnBlack = this.smokeLeftLungs.animations.add("turnBlack", Sprites.reversedSpriteFrames(8, 4))

        oneOffs.add(6000, () => {
            turnBlack.play(2)
        })

        // TODO createWithBulletedText an alphaMask for this
        oneOffs.add(100, () => {
            this.smokeLungs(loopsTimer, this.smokeSmokeLeft, true)
        })

        oneOffs.add(9000, () => {
            this.smokeLungs(loopsTimer, this.smokeSmokeRight)
        })

        oneOffs.add(11000, () => {
            ColorUtils.shadeInALoop(this.smokeBoxFront, loopsTimer, 200, "#55552b", 0.2)
        })

        oneOffs.add(15000, () => {
            ColorUtils.shadeInALoop(this.smokeRightLungs, loopsTimer, 200, "#55552b", 0.2)
        })

        oneOffs.add(17500, () => {
            const cigSmokeCloud = this.game.add.image(SvdPos.cigSmokeCloudX(), SvdPos.cigSmokeCloudY(),
                SecondVsDirectAssets.SMOKE_CIRCLE)
            cigSmokeCloud.anchor.setTo(0.5, 0.5)
            Scale.scaleSprite(cigSmokeCloud, 0.05)
            cigSmokeCloud.alpha = 0.3
            CameraZoom.zoomSprite(cigSmokeCloud, null, null, 1, 3000)
            Sprites.fade(this.smokeBoxSmoke, {fadeDuration: 3000, fadeAmount: 0.5})
        })

        smokeAudio.onStop.addOnce(() => {
            loopsTimer.stop()
            this.backToTable(undoZoom)
            this.smokeBoxFront.tint = colorNum.gray

            this.cig.animations.stop()
            this.cig.frame = 0
            this.smokeSmokeLeft.visible = false
            this.smokeSmokeRight.visible = false
        })
    }

    vapor = () => {
        const undoZoom = CameraZoom.zoom(this.game, this.vaporBoxFront)
        const vapeAudio = this.game.add.audio(SecondVsDirectAssets.VAPING_AUDIO)
        const oneOffs = new AudioSync(this.game, vapeAudio)
        const loopsTimer = this.game.time.create(false)
        this.playAudioAndZoom(vapeAudio, oneOffs, loopsTimer)

        oneOffs.add(2000, () => {
            this.smokeLungs(loopsTimer, this.vaporSmokeLeft, true)
        })

        oneOffs.add(3000, () => {
            ColorUtils.shadeInALoop(this.vaporLeftLungs, loopsTimer, 300, "#4c2a63", 0.2)
        })

        const vapeSmoke = this.game.add.image(SvdPos.vapeSmokeX(), SvdPos.vapeSmokeY(), SecondVsDirectAssets.VAPE_SMOKE)
        Scale.scaleSprite(vapeSmoke, 0.9)
        vapeSmoke.alpha = 0

        oneOffs.add(9000, () => {
            this.smokeLungs(loopsTimer, this.vaporSmokeRight)
        })

        oneOffs.add(10500, () => {
            ColorUtils.shadeInALoop(this.vaporRightLungs, loopsTimer, 300, "#4c2a63", 0.5)
        })

        oneOffs.add(12000, () => {
            Sprites.fade(vapeSmoke)
        })
        oneOffs.add(13000, () => {
            Sprites.fadeOut(vapeSmoke)
        })
        oneOffs.add(16000, () => {
            Sprites.fade(vapeSmoke)
        })
        oneOffs.add(17000, () => {
            Sprites.fadeOut(vapeSmoke)
        })
        oneOffs.add(20000, () => {
            Sprites.fade(vapeSmoke)
        })
        oneOffs.add(21000, () => {
            Sprites.fadeOut(vapeSmoke)
        })
        oneOffs.add(24000, () => {
            Sprites.fade(vapeSmoke)
        })
        oneOffs.add(25000, () => {
            Sprites.fadeOut(vapeSmoke)
        })

        vapeAudio.onStop.addOnce(() => {
            loopsTimer.stop()
            this.backToTable(undoZoom)
            this.vaporBoxFront.tint = colorNum.gray
        })
    }

    backToTable = (undoZoom: ZoomInfo) => {
        if (this.oneDone) {
            log.debug("Done with both parts of second vs. direct.")
            const backToLabTimer = this.game.time.create()
            backToLabTimer.add(2000, () => {
                CameraZoom.instaZoom(undoZoom)
                SecondHandRepo.instance.setFirstHandVsSecondHandComplete()
                this.game.state.start(ActivityKey.SECOND_HAND)
            })
            backToLabTimer.start()
        } else {
            log.debug("Done with one part of second vs. direct.")
            CameraZoom.instaZoom(undoZoom)
            this.oneDone = true
        }
    }

    smokeLungs = (timer: Phaser.Timer, smokeSprite: Phaser.Sprite, flip = false) => {

        timer.loop(5000, () => {
            smokeSprite.visible = true
            smokeSprite.alpha = 0.5
            const smoke = smokeSprite.animations.add("smoke", flip ? Sprites.reversedSpriteFrames(9, 4) : null)
            smoke.onComplete.addOnce(() => {
                smokeSprite.visible = false
            })
            smoke.play(10)
        })

    }

}
